// Edit a vendor purchase order — draft or already sent.
//
// TWO PATHS, because a PO a vendor already holds is a different object from one
// that has never left the building.
//
//   draft  -> POST /regenerate   re-renders in place. No revision, no reason:
//                                nobody has seen it, so there is nothing to
//                                supersede and nothing to explain.
//   sent   -> POST /revise       archives the exact document the vendor
//                                received, bumps the revision, and returns the
//                                PO to draft so you send Rev B deliberately.
//
// WHY SENT POs ARE AMENDED RATHER THAN REISSUED. The PO number is the key the
// vendor's system, their invoice, our receiving and any export paperwork all
// reference. A second number to fix the first orphans that chain. (A commercial
// invoice is the opposite case — there a new number is correct, and the app
// models it that way.) What makes amending safe is that nothing is destroyed:
// every superseded document is kept byte-for-byte.
//
// The change reason is required by the server AND by the database. It is not a
// formality — it is the line someone reads two years from now when they are
// trying to work out why the vendor has two copies.

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, AlertTriangle, History, Save } from 'lucide-react';
import {
  api,
  type VendorPurchaseOrder,
  type RegenerateVendorPoResponse,
  type ReviseVendorPoResponse,
  type VendorPoRevisionsResponse,
} from '@/lib/api';
import { ShipFromPicker, vendorPoShipToText } from '@/components/ship-from-locations';

interface Props {
  po: VendorPurchaseOrder;
  onClose: () => void;
}

/** 1 -> A, 2 -> B. Matches the document, which uses letters because "Rev 2"
 *  reads like a quantity to someone scanning a stack of paper. */
function revLetter(n: number): string {
  if (n < 1) return String(n);
  let out = '', v = n;
  while (v > 0) { const r = (v - 1) % 26; out = String.fromCharCode(65 + r) + out; v = Math.floor((v - 1) / 26); }
  return out;
}

const inputCls =
  'w-full rounded border border-border bg-surface px-2 py-1 text-xs text-foreground ' +
  'focus:outline-none focus:ring-1 focus:ring-primary';

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-foreground-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-foreground-muted mt-0.5">{hint}</span>}
    </label>
  );
}

export function EditVendorPoModal({ po, onClose }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const isDraft = po.status === 'draft';
  const isCancelled = po.status === 'cancelled';

  const [qty, setQty] = useState(String(po.qty));
  const [shipToText, setShipToText] = useState(po.shipToText ?? '');
  const [shipToMode, setShipToMode] = useState(po.shipToMode);
  const [paymentTerms, setPaymentTerms] = useState(po.paymentTerms ?? '');
  const [certsRequired, setCertsRequired] = useState(po.certsRequired ?? '');
  const [notes, setNotes] = useState(po.notes ?? '');
  const [changeReason, setChangeReason] = useState('');
  const [done, setDone] = useState<{ revision?: number; supersededRevision?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const history = useQuery({
    queryKey: ['vendor-po-revisions', po.id],
    queryFn: () =>
      api<VendorPoRevisionsResponse>(`/api/vendor-purchase-orders/${po.id}/revisions`, {
        tokenGetter: () => getToken(),
      }),
  });

  const body = () => ({
    qty: Number(qty),
    shipToMode,
    shipToText: shipToText.trim(),
    paymentTerms: paymentTerms.trim() || undefined,
    certsRequired: certsRequired.trim() || undefined,
    notes: notes.trim() || undefined,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isDraft) {
        return api<RegenerateVendorPoResponse>(
          `/api/vendor-purchase-orders/${po.id}/regenerate`,
          { method: 'POST', tokenGetter: () => getToken(), body: JSON.stringify(body()) },
        ).then(() => ({}));
      }
      return api<ReviseVendorPoResponse>(
        `/api/vendor-purchase-orders/${po.id}/revise`,
        {
          method: 'POST',
          tokenGetter: () => getToken(),
          body: JSON.stringify({ ...body(), changeReason: changeReason.trim() }),
        },
      );
    },
    onSuccess: (r) => {
      setDone(r as { revision?: number; supersededRevision?: number });
      setError(null);
      void qc.invalidateQueries({ queryKey: ['vendor-pos'] });
      void qc.invalidateQueries({ queryKey: ['rfq'] });
      void qc.invalidateQueries({ queryKey: ['vendor-po-revisions', po.id] });
    },
    onError: (e: unknown) =>
      setError((e as { message?: string })?.message ?? 'The change could not be saved.'),
  });

  const qtyValid = Number.isInteger(Number(qty)) && Number(qty) >= 1;
  const reasonValid = isDraft || changeReason.trim().length >= 3;
  const canSave = qtyValid && shipToText.trim().length > 0 && reasonValid && !isCancelled;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="w-full max-w-2xl rounded-lg bg-surface shadow-lg my-8">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              {isDraft ? 'Edit draft PO' : 'Revise purchase order'} · {po.ref}
            </h2>
            <p className="text-[11px] text-foreground-muted mt-0.5">
              {po.vendorName ?? '—'} · currently revision {revLetter(po.revision ?? 1)} · {po.status}
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3 text-xs">
          {done ? (
            <div className="space-y-3">
              <div className="rounded bg-success-muted p-2 text-success font-medium">
                {isDraft
                  ? 'Draft updated.'
                  : `Revision ${revLetter(done.revision ?? 2)} created, superseding ${
                      revLetter(done.supersededRevision ?? 1)}.`}
              </div>
              {!isDraft && (
                <div className="rounded bg-warning-muted p-2 text-foreground-muted">
                  <div className="flex items-center gap-1.5 text-warning font-medium mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Not sent yet
                  </div>
                  The PO is back to draft because revision {revLetter(done.revision ?? 2)} has not
                  been sent to anyone. Review the document, then use <strong>Mark sent</strong> to
                  issue it. The vendor keeps the same PO number.
                </div>
              )}
              <button onClick={onClose} className="btn-primary text-xs px-3 py-1">Done</button>
            </div>
          ) : isCancelled ? (
            <div className="rounded bg-surface-muted p-3 text-foreground-muted">
              This PO is cancelled. Revising it would imply it is still live — generate a new PO
              instead.
            </div>
          ) : (
            <>
              {!isDraft && (
                <div className="rounded bg-warning-muted p-2">
                  <div className="flex items-center gap-1.5 text-warning font-medium">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    This PO has already been sent
                  </div>
                  <div className="text-foreground-muted mt-1">
                    Saving creates <strong>revision {revLetter((po.revision ?? 1) + 1)}</strong>. The
                    PO number stays the same, the document the vendor already has is kept exactly as
                    it was sent, and the new one states what it replaces.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Quantity">
                  <input className={inputCls} inputMode="numeric" value={qty}
                    onChange={(e) => setQty(e.target.value)} />
                </Field>
                <Field label="Ship-to mode">
                  <select className={inputCls} value={shipToMode}
                    onChange={(e) => setShipToMode(e.target.value as typeof shipToMode)}>
                    <option value="customer_direct">Drop-ship to customer</option>
                    <option value="logistics_partner">To our logistics partner (3PL)</option>
                    <option value="apd_warehouse">To our warehouse</option>
                  </select>
                </Field>
              </div>

              {shipToMode === 'logistics_partner' && (
                <Field label="Fill from a saved logistics partner">
                  <ShipFromPicker value="" noneLabel="Choose…" className={inputCls}
                    onPick={(l) => { if (l) setShipToText(vendorPoShipToText(l)); }} />
                </Field>
              )}

              <Field label="Ship-to address" hint="Exactly as it should appear on the PO">
                <textarea className={inputCls} rows={4} value={shipToText}
                  onChange={(e) => setShipToText(e.target.value)} />
              </Field>

              <Field label="Payment terms">
                <textarea className={inputCls} rows={2} value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)} />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Certification required">
                  <input className={inputCls} value={certsRequired}
                    onChange={(e) => setCertsRequired(e.target.value)} />
                </Field>
                <Field label="Notes">
                  <input className={inputCls} value={notes}
                    onChange={(e) => setNotes(e.target.value)} />
                </Field>
              </div>

              {!isDraft && (
                <Field
                  label="Why is this being revised? (required)"
                  hint="Goes on the permanent record, not on the document the vendor sees."
                >
                  <input
                    className={inputCls}
                    value={changeReason}
                    placeholder="e.g. Ship-to was a placeholder; corrected to the real consignee"
                    onChange={(e) => setChangeReason(e.target.value)}
                  />
                </Field>
              )}

              {history.data && history.data.revisions.length > 0 && (
                <div className="rounded border border-border p-2">
                  <div className="flex items-center gap-1.5 font-medium text-foreground mb-1">
                    <History className="w-3.5 h-3.5" />
                    Previous revisions
                  </div>
                  <ul className="space-y-1">
                    {history.data.revisions.map((r) => (
                      <li key={r.publicId} className="text-foreground-muted">
                        <span className="font-mono">Rev {revLetter(r.revision)}</span> ·{' '}
                        {r.statusAtSupersede === 'sent' ? 'was sent' : 'never sent'}
                        {r.sentAt && ` ${new Date(r.sentAt).toLocaleDateString()}`} ·{' '}
                        {r.changeReason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {error && <div className="text-danger">{error}</div>}

              <div className="flex items-center justify-between border-t border-border pt-2">
                <span className="text-foreground-muted">
                  {!isDraft && !reasonValid && 'A reason is required to revise a sent PO.'}
                  {!qtyValid && 'Quantity must be a whole number of at least 1.'}
                </span>
                <div className="flex gap-2">
                  <button onClick={onClose} className="btn-ghost text-xs px-3 py-1">Cancel</button>
                  <button
                    onClick={() => save.mutate()}
                    disabled={!canSave || save.isPending}
                    className="btn-primary text-xs px-3 py-1 flex items-center gap-1 disabled:opacity-50"
                  >
                    <Save className="w-3 h-3" />
                    {save.isPending
                      ? 'Saving…'
                      : isDraft
                        ? 'Save draft'
                        : `Create revision ${revLetter((po.revision ?? 1) + 1)}`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
